import EditTimeForm from 'components/AvailabilityTemplateModalForm/EditForm/EditTimeForm';
import { ModalWrapper } from 'components/AvailabilityTemplateModalForm/style';
import {
  ClientAvailabilityEvent,
  ClientBlockAvailabilityEvent
} from 'model/v2/availabilityTemplate';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import FormItem from 'api/sharedComponents/reactHookFormComponents/formItem';
import { mapToLocationsDownOption } from 'utils/mappers/form';
import ModalBottomWithDelete from 'components/AvailabilityTemplateModalForm/ReusableComponents/ModalBottomWithDelete';
import { ILocation } from 'model/v2/location';
import { FrequencyFormWrapper } from 'pages/MainCalendarPage/AppointmentForm/style';
import { OvalCheckboxGroup } from 'lib/ui';
import { WEEK_DAYS } from 'utils/constants/lists';
import { yupResolver } from '@hookform/resolvers/yup';
import { CLIENT_EDIT_AVAILABILITY_BLOCK } from 'utils/validators/clients';
import { Checkbox } from 'semantic-ui-react';
import { Alert } from '@material-ui/lab';
import { Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { Box, Tabs, Tab } from '@material-ui/core';
import MultiSelectDropdown from '../../../../../containers/form/multiSelectDropdown/multiSelectDropdown';
import { ISchedulingShiftDropdownOptions } from 'model/v2';
import { formatBlockLabel } from 'pages/ConfigurationsPage/addAvailabilityTemplate/utils';

interface IProps {
  onClose(): void;
  onSubmit(data: ITemplateForm, oldEvent: ClientAvailabilityEvent): void;
  onDelete(selectedEvent: ClientAvailabilityEvent): void;
  data: any;
  locations: ILocation[];
  setLocationChecked: Dispatch<SetStateAction<number>>;
  locationChecked: number;
  setBlockLocationChecked: Dispatch<SetStateAction<number>>;
  blockLocationChecked: number;
  setSelectedBlock: Dispatch<SetStateAction<string[]>>;
  selectedBlock: string[];
  schedulingShiftsData: ISchedulingShiftDropdownOptions[];
  isSchedulingBlocksLoading: boolean;
  onShiftSubmit?: (
    blockForm: ClientBlockAvailabilityEvent,
    selectedEvent: ClientAvailabilityEvent,
    schedulingShiftsData: ISchedulingShiftDropdownOptions[]
  ) => void;
}
interface ITemplateForm {
  start: Date;
  end: Date;
  location: number;
  recurringDays: number[];
}
const EditAvailabilityBlockForm: React.FC<IProps> = ({
  onClose,
  onSubmit,
  onDelete,
  data,
  locations,
  setLocationChecked,
  locationChecked,
  setBlockLocationChecked,
  blockLocationChecked,
  setSelectedBlock,
  selectedBlock,
  schedulingShiftsData,
  isSchedulingBlocksLoading,
  onShiftSubmit
}) => {
  const dayIndex = data.start.getDay();
  const blockOptions = schedulingShiftsData
    .flatMap(shift =>
      shift.blocks.map(block => ({
        label: formatBlockLabel(block.days!, block.startTime!, block.endTime!),
        value: block.id!.toString()
      }))
    )
    .filter(
      (option, index, self) =>
        index === self.findIndex(o => o.label === option.label)
    );
  const locationsOptions = useMemo(() => {
    return mapToLocationsDownOption(locations || []);
  }, [locations]);
  const methods = useForm<ITemplateForm>({
    defaultValues: {
      start: data.start,
      end: data.end,
      location: data.location || (locations && locations[0].id),
      recurringDays: [dayIndex]
    },
    resolver: yupResolver(CLIENT_EDIT_AVAILABILITY_BLOCK)
  });
  const recurringDaysHandler = useCallback(
    (val: number[]) => {
      methods.setValue('recurringDays', val);
    },
    [methods]
  );
  const onHandleCheckChange = (id: number, isChecked?: boolean) => {
    if (isChecked) {
      setLocationChecked(locationChecked + id);
    } else {
      setLocationChecked(locationChecked - id);
    }
  };
  useEffect(() => {
    if (data.blockId) {
      setSelectedBlock([data.blockId.toString()]);
      setBlockLocationChecked(data.location || 0);
    }
  }, [data, setSelectedBlock, setBlockLocationChecked]);
  const onHandleBlockCheckChange = (id: number, isChecked?: boolean) => {
    if (isChecked) {
      setBlockLocationChecked(blockLocationChecked + id);
    } else {
      setBlockLocationChecked(blockLocationChecked - id);
    }
  };
  const submitForm = useCallback(
    (submittedData: ITemplateForm) => {
      if (locationChecked === 0) locationChecked = locations[0].id!;
      submittedData.location = locationChecked;
      onSubmit(submittedData, data);
    },
    [onSubmit, data, locationChecked]
  );
  const [showAlert, setShowAlert] = useState(true);
  const [tabValue, setTabValue] = useState(data.blockId ? 1 : 0); // 0 = General, 1 = Block

  useEffect(() => {
    if (
      tabValue === 1 &&
      locationsOptions.length > 0 &&
      blockLocationChecked === 0
    ) {
      setBlockLocationChecked(locationsOptions[0].value);
    }
  }, [tabValue, locationsOptions, blockLocationChecked]);

  return (
    <FormProvider {...methods}>
      {showAlert && (
        <Alert
          severity="info"
          onClose={() => setShowAlert(false)}
          style={{
            background: '#E4D9FD',
            color: '#000000',
            marginBottom: '20px',
            marginRight: '20px',
            marginLeft: '20px'
          }}
        >
          <Typography style={{ color: '#000000' }}>
            If you switch between General and Block Availability without
            confirming, your current changes will not be saved.
          </Typography>
        </Alert>
      )}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          marginBottom: 20,
          marginLeft: 20
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          TabIndicatorProps={{ style: { background: '#6F42F5', height: 3 } }}
          style={{ minHeight: 36 }}
        >
          <Tab
            label="General Availability"
            style={{
              color: tabValue === 0 ? '#6F42F5' : '#666',
              fontWeight: tabValue === 0 ? 600 : 400,
              minWidth: 180,
              minHeight: 36,
              textTransform: 'none'
            }}
          />
          <Tab
            label="Block Availability"
            style={{
              color: tabValue === 1 ? '#6F42F5' : '#666',
              fontWeight: tabValue === 1 ? 600 : 400,
              minWidth: 180,
              minHeight: 36,
              textTransform: 'none'
            }}
          />
        </Tabs>
      </Box>
      <ModalWrapper style={{ paddingLeft: 20, paddingRight: 20 }}>
        {tabValue === 0 && (
          <>
            <div className="upper-formItem-wrapper">
              <div className="upper-formItem">
                <div className="time-formItem">
                  <EditTimeForm
                    time={data.start}
                    controlName={'start'}
                    displayName={'Start Time'}
                    dayIndex={dayIndex}
                  ></EditTimeForm>
                  <EditTimeForm
                    time={data.end}
                    controlName={'end'}
                    displayName={'End Time'}
                    dayIndex={dayIndex}
                  ></EditTimeForm>
                </div>
              </div>
              <div className="upper-formItem">
                <FormItem label="Location">
                  <Controller
                    control={methods.control}
                    name={'location'}
                    render={() => (
                      <>
                        {locationsOptions.map((location: any) => {
                          return (
                            <div className="saved_filters" key={location.id}>
                              <Checkbox
                                className="saved-filters_checkbox"
                                label={location.label}
                                name={location.label}
                                value={location.value}
                                checked={(location.value & locationChecked) > 0}
                                onChange={(_e, checkData) =>
                                  onHandleCheckChange(
                                    location.value!,
                                    checkData.checked
                                  )
                                }
                              />
                            </div>
                          );
                        })}
                      </>
                    )}
                  />
                </FormItem>
              </div>
            </div>
            <FrequencyFormWrapper style={{ paddingLeft: 0, paddingRight: 0 }}>
              <h3>Apply To</h3>
              <FormItem optional={false} label="Days" className="daysWrapper">
                <Controller
                  name="recurringDays"
                  control={methods.control}
                  render={fieldProps => (
                    <OvalCheckboxGroup
                      options={WEEK_DAYS}
                      field={fieldProps}
                      defaultValue={[dayIndex]}
                      errors={methods.errors}
                      children={null}
                      onChange={recurringDaysHandler}
                    />
                  )}
                />
              </FormItem>
            </FrequencyFormWrapper>
            <ModalBottomWithDelete
              onClose={onClose}
              submitForm={submitForm}
              onDelete={onDelete}
              selectedEvent={data}
            />
          </>
        )}
        {tabValue === 1 && (
          <>
            <FormItem label="Accepted Blocks">
              <Box style={{ marginBottom: 8 }}>
                <MultiSelectDropdown
                  label="Select Blocks"
                  placeholder="Select Blocks"
                  options={blockOptions}
                  selected={selectedBlock}
                  loading={isSchedulingBlocksLoading}
                  onApply={selected => {
                    setSelectedBlock(selected);
                  }}
                  disabled={data.blockId}
                />
              </Box>
            </FormItem>
            <FormItem label="Location">
              <Controller
                control={methods.control}
                name={'location'}
                render={() => (
                  <>
                    {locationsOptions.map((location: any) => {
                      return (
                        <div className="saved_filters" key={location.id}>
                          <Checkbox
                            className="saved-filters_checkbox"
                            label={location.label}
                            name={location.label}
                            value={location.value}
                            checked={
                              (location.value & blockLocationChecked) > 0
                            }
                            onChange={(_e, checkData) =>
                              onHandleBlockCheckChange(
                                location.value!,
                                checkData.checked
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              />
            </FormItem>
            <Box
              display="flex"
              justifyContent="space-between"
              borderTop="1px solid #eee"
              pt={2}
              pb={2}
            >
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'red',
                  marginRight: 16,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
                onClick={() => onDelete(data)}
              >
                Delete
              </button>

              <Box>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    marginRight: 16,
                    fontSize: 16,
                    cursor: 'pointer'
                  }}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={{
                    background:
                      selectedBlock.length && blockLocationChecked
                        ? '#6F42F5'
                        : '#eee',
                    color:
                      selectedBlock.length && blockLocationChecked
                        ? '#fff'
                        : '#aaa',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor:
                      selectedBlock.length && blockLocationChecked
                        ? 'pointer'
                        : 'not-allowed',
                    opacity:
                      selectedBlock.length && blockLocationChecked ? 1 : 0.7
                  }}
                  disabled={!(selectedBlock.length && blockLocationChecked)}
                  onClick={() => {
                    if (selectedBlock.length && blockLocationChecked) {
                      onShiftSubmit?.(
                        {
                          blocks: selectedBlock,
                          location: blockLocationChecked
                        },
                        data,
                        schedulingShiftsData
                      );
                    }
                  }}
                >
                  Confirm
                </button>
              </Box>
            </Box>
          </>
        )}
      </ModalWrapper>
    </FormProvider>
  );
};

export default EditAvailabilityBlockForm;
